import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BLOCK_TYPES } from "@/components/PageRenderer";
import { MediaPickerDialog } from "@/pages/AdminMediaLibrary";
import { Plus, Trash2, ArrowUp, ArrowDown, Pencil, FileText, Image as ImageIcon } from "lucide-react";

const TEXTAREA_FIELDS = ["text", "body", "sub", "caption"];
const EMPTY = { slug: "", title: "", status: "draft", blocks: [], seo: {} };

const newBlock = (type) => {
  const props = {};
  if (type === "gallery") props.images = [];
  if (type === "stats") props.items = [{ n: "", l: "" }];
  return { type, props, _uid: Math.random().toString(36).slice(2) };
};

function FieldEditor({ field, value, onChange, onPickImage }) {
  if (field === "images") {
    const list = value || [];
    return (
      <div>
        <Label className="text-xs mb-1 block">Gallery images</Label>
        <div className="space-y-2">
          {list.map((u, i) => (
            <div key={i} className="flex gap-2">
              <Input value={u} onChange={(e) => onChange(list.map((x, idx) => (idx === i ? e.target.value : x)))} className="rounded-lg h-9 text-xs" />
              <Button size="sm" variant="ghost" onClick={() => onChange(list.filter((_, idx) => idx !== i))} className="h-9 text-destructive"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => onPickImage((url) => onChange([...(list), url]))} className="rounded-full"><ImageIcon className="h-3.5 w-3.5 mr-1" /> Add image</Button>
        </div>
      </div>
    );
  }
  if (field === "items") {
    const list = value || [];
    return (
      <div>
        <Label className="text-xs mb-1 block">Stats</Label>
        <div className="space-y-2">
          {list.map((it, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="Number" value={it.n} onChange={(e) => onChange(list.map((x, idx) => (idx === i ? { ...x, n: e.target.value } : x)))} className="rounded-lg h-9 text-xs w-28" />
              <Input placeholder="Label" value={it.l} onChange={(e) => onChange(list.map((x, idx) => (idx === i ? { ...x, l: e.target.value } : x)))} className="rounded-lg h-9 text-xs" />
              <Button size="sm" variant="ghost" onClick={() => onChange(list.filter((_, idx) => idx !== i))} className="h-9 text-destructive"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => onChange([...(list), { n: "", l: "" }])} className="rounded-full"><Plus className="h-3.5 w-3.5 mr-1" /> Add stat</Button>
        </div>
      </div>
    );
  }
  const isImageUrl = field === "url";
  return (
    <div>
      <Label className="text-xs mb-1 block capitalize">{field}</Label>
      {TEXTAREA_FIELDS.includes(field) ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
      ) : (
        <div className="flex gap-2">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-lg h-9 text-sm" />
          {isImageUrl && <Button size="sm" variant="outline" onClick={() => onPickImage((url) => onChange(url))} className="rounded-full whitespace-nowrap"><ImageIcon className="h-3.5 w-3.5 mr-1" /> Pick</Button>}
        </div>
      )}
    </div>
  );
}

export default function AdminPagesCMS() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null); // null=list, {} = form
  const [form, setForm] = useState(EMPTY);
  const [addType, setAddType] = useState("section");
  const [pickerCb, setPickerCb] = useState(null);

  const load = () => api.get("/admin/pages").then((r) => setPages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = async (id) => {
    const r = await api.get(`/admin/pages/${id}`);
    const blocks = (r.data.blocks || []).map((b) => ({ ...b, _uid: Math.random().toString(36).slice(2) }));
    setForm({ ...EMPTY, ...r.data, blocks }); setEditing(id);
  };

  const setBlockProp = (i, field, val) =>
    setForm((f) => ({ ...f, blocks: f.blocks.map((b, idx) => (idx === i ? { ...b, props: { ...b.props, [field]: val } } : b)) }));
  const move = (i, dir) => setForm((f) => {
    const arr = [...f.blocks]; const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...f, blocks: arr };
  });
  const addBlock = () => setForm((f) => ({ ...f, blocks: [...f.blocks, newBlock(addType)] }));
  const removeBlock = (i) => setForm((f) => ({ ...f, blocks: f.blocks.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const payload = { slug: form.slug || form.title, title: form.title, status: form.status, blocks: form.blocks.map(({ _uid, ...b }) => b), seo: form.seo || {} };
    try {
      if (editing === "new") await api.post("/admin/pages", payload);
      else await api.put(`/admin/pages/${editing}`, payload);
      toast.success("Page saved");
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const del = async (id) => { await api.delete(`/admin/pages/${id}`); toast.success("Page deleted"); load(); };

  if (editing === null) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6" data-testid="admin-pages-panel">
        <div className="flex items-center justify-between mb-4">
          <div><h3 className="font-serif text-lg font-semibold">Pages</h3><p className="text-xs text-muted-foreground">Edit About & Legal, or build new pages served at /p/&lt;slug&gt;.</p></div>
          <Button onClick={openNew} data-testid="page-new-btn" className="rounded-full bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1.5" /> New page</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Tip: use slug <b>about</b> to override the About page, and <b>legal-terms</b>, <b>legal-privacy</b>, <b>legal-cookies</b> for the legal docs. Set status to <b>active</b> to publish.</p>
        <div className="space-y-2">
          {pages.length === 0 && <p className="text-sm text-muted-foreground">No pages yet.</p>}
          {pages.map((p) => (
            <div key={p.id} data-testid={`page-row-${p.slug}`} className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
              <div className="min-w-0">
                <p className="font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-accent" /> {p.title} <span className="text-xs text-muted-foreground">/{p.slug}</span></p>
                <span className={`text-xs ${p.status === "active" ? "text-emerald-600" : "text-muted-foreground"}`}>{p.status}</span>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(p.id)} data-testid={`page-edit-${p.slug}`} className="h-8"><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(p.id)} data-testid={`page-delete-${p.slug}`} className="h-8 text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="admin-page-editor">
      <MediaPickerDialog open={!!pickerCb} onOpenChange={(o) => !o && setPickerCb(null)} onSelect={(url) => { pickerCb && pickerCb(url); setPickerCb(null); }} />
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-lg font-semibold">{editing === "new" ? "New page" : "Edit page"}</h3>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
          <Button onClick={save} data-testid="page-save-btn" className="rounded-full bg-primary text-primary-foreground">Save</Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div><Label className="text-xs mb-1 block">Title</Label><Input data-testid="page-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-lg" /></div>
        <div><Label className="text-xs mb-1 block">Slug</Label><Input data-testid="page-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="about / legal-terms" className="rounded-lg" /></div>
        <div><Label className="text-xs mb-1 block">Status</Label>
          <select data-testid="page-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm">
            <option value="draft">Draft</option><option value="active">Active (live)</option><option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {form.blocks.map((b, i) => {
          const def = BLOCK_TYPES.find((t) => t.type === b.type);
          return (
            <div key={b._uid || i} data-testid={`page-block-${i}`} className="rounded-xl border border-border p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold capitalize flex items-center gap-2">{b.type}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => move(i, -1)} className="h-8"><ArrowUp className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => move(i, 1)} className="h-8"><ArrowDown className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => removeBlock(i)} data-testid={`page-block-remove-${i}`} className="h-8 text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="grid gap-3">
                {(def?.fields || []).map((field) => (
                  <FieldEditor key={field} field={field} value={b.props?.[field]} onChange={(val) => setBlockProp(i, field, val)} onPickImage={(cb) => setPickerCb(() => cb)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 mt-5">
        <select value={addType} onChange={(e) => setAddType(e.target.value)} data-testid="page-block-type-select" className="h-10 rounded-lg border border-border bg-background px-3 text-sm">
          {BLOCK_TYPES.map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
        </select>
        <Button variant="outline" onClick={addBlock} data-testid="page-add-block-btn" className="rounded-full border-accent"><Plus className="h-4 w-4 mr-1.5" /> Add block</Button>
      </div>
    </div>
  );
}
